/**
 * BELÉPÉS ÜGYFÉLKÉNT (#76) — az admin oldala: indítás és lezárás.
 *
 * MŰVELET, NEM OLDAL (`CLAUDE.md` §2): a parancspalettán él. A cég a
 * felületen kiválasztott cég (#73) — ha nincs kiválasztva, a parancs a
 * cégválasztót nyitja, és a könyvelő utána újra indítja.
 *
 * ═══ AZ ÚJ LAP SZINKRON NYÍLIK ═══
 *
 * A böngésző csak a felhasználói gesztusban (kattintás, Enter) nyitott lapot
 * engedi át. A szerver válasza után nyitott lapot a felugróablak-blokkoló
 * elnyelné. Ezért a lap AZONNAL nyílik, üresen, és a cím a válasz után kerül
 * bele. Ha a blokkoló mégis elnyelte, a közlemény adja a linket — a kód 60
 * másodpercig él, addig kattintható.
 *
 * Az `opener` nullázva: az ügyfél felülete ne érhesse el az admin ablakát.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/features/auth/AuthProvider';
import type { Command } from '@/features/palette/commands';
import { ApiError, api, type Me } from '@/lib/api-client';

export interface ActiveImpersonation {
  grantId: string;
  companyId: string;
  companyName: string;
  expiresAt: string;
}

interface ImpersonationStatus {
  allowed: boolean;
  active: ActiveImpersonation | null;
}

interface StartResponse {
  grantId: string;
  expiresAt: string;
  handoffUrl: string;
}

export interface ImpersonationHook {
  /** Indíthat-e egyáltalán — a palettán csak ekkor jelenik meg a parancs. */
  allowed: boolean;
  active: ActiveImpersonation | null;
  /** A szerver üzenete, ha az indítás vagy a lezárás elbukott. */
  error: string | null;
  /** Ha a felugróablak-blokkoló elnyelte a lapot: a link, amíg a kód él. */
  blockedHandoff: string | null;
  busy: boolean;
  start: (companyId: string) => void;
  end: () => void;
  dismiss: () => void;
}

/** A `setTimeout` felső korlátja — fölötte a böngésző azonnal futtatna. */
const MAX_TIMEOUT_MS = 2_147_483_647;

/** Irodai felhasználó-e — az ügyfél és a maszk itt nem kérdez. */
function isOffice(me: Me | null): boolean {
  return me?.actor.kind === 'user' && me.actor.role !== 'CLIENT';
}

/**
 * Az üres lap megnyitása a gesztusban. `null`, ha a blokkoló elnyelte.
 *
 * ⚠️ NEM `noopener` a `window.open` harmadik paraméterében: azzal a hívás
 * `null`-t ad vissza, és a címet nem tudnánk beleírni. Helyette kézzel
 * nullázzuk az `opener`-t, mielőtt bármi betöltődne a lapra.
 */
function openBlankTab(): Window | null {
  const tab = window.open('about:blank', '_blank');
  if (tab) {
    tab.opener = null;
  }
  return tab;
}

export function useImpersonation(me: Me | null): ImpersonationHook {
  const { signOut } = useAuth();
  const office = isOffice(me);
  const [status, setStatus] = useState<ImpersonationStatus>({ allowed: false, active: null });
  const [error, setError] = useState<string | null>(null);
  const [blockedHandoff, setBlockedHandoff] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);

  const fail = useCallback(
    (caught: unknown) => {
      if (caught instanceof ApiError && caught.status === 401) {
        signOut('expired');
        return;
      }
      setError(caught instanceof Error ? caught.message : String(caught));
    },
    [signOut]
  );

  const reload = useCallback(() => {
    if (!office) {
      setStatus({ allowed: false, active: null });
      return;
    }
    void api<ImpersonationStatus>('/impersonations/me')
      .then(setStatus)
      // Az állapot hiánya nem hiba a felületen: a parancs egyszerűen nem
      // jelenik meg. Ha a szerver valóban nem érhető el, a többi felület
      // úgyis szól.
      .catch(() => setStatus({ allowed: false, active: null }));
  }, [office]);

  useEffect(() => {
    reload();
  }, [reload]);

  /**
   * A KÖZLEMÉNY NE AVULJON EL (#76). Egy másik lapon indított, az ügyfél-
   * felületen lezárt vagy lejárt maszk ne maradjon „belépve" állapotban, és
   * egy itt nem látott, nyitott maszk se maradjon láthatatlan. Fókuszkor és a
   * lejárat pillanatában újraolvas — a kliens-oldali sáv mintájára.
   */
  const expiresAt = status.active?.expiresAt ?? null;
  useEffect(() => {
    if (!office) {
      return;
    }
    window.addEventListener('focus', reload);
    const until =
      expiresAt === null
        ? null
        : Math.min(MAX_TIMEOUT_MS, Math.max(0, new Date(expiresAt).getTime() - Date.now()) + 1000);
    const timer = until === null ? undefined : window.setTimeout(reload, until);
    return () => {
      window.removeEventListener('focus', reload);
      window.clearTimeout(timer);
    };
  }, [office, expiresAt, reload]);

  const start = useCallback(
    (companyId: string) => {
      if (inFlight.current) {
        return;
      }
      inFlight.current = true;
      setBusy(true);
      setError(null);
      setBlockedHandoff(null);

      const tab = openBlankTab();

      void api<StartResponse>('/impersonations', {
        method: 'POST',
        body: JSON.stringify({ companyId }),
      })
        .then((started) => {
          if (tab && !tab.closed) {
            tab.location.replace(started.handoffUrl);
          } else {
            setBlockedHandoff(started.handoffUrl);
          }
          reload();
        })
        .catch((caught: unknown) => {
          tab?.close();
          fail(caught);
        })
        .finally(() => {
          inFlight.current = false;
          setBusy(false);
        });
    },
    [fail, reload]
  );

  const end = useCallback(() => {
    const grantId = status.active?.grantId;
    if (!grantId || inFlight.current) {
      return;
    }
    inFlight.current = true;
    setBusy(true);
    setError(null);
    void api<void>(`/impersonations/${grantId}`, { method: 'DELETE' })
      .then(reload)
      .catch(fail)
      .finally(() => {
        inFlight.current = false;
        setBusy(false);
      });
  }, [status.active, fail, reload]);

  const dismiss = useCallback(() => {
    setError(null);
    setBlockedHandoff(null);
  }, []);

  return {
    allowed: status.allowed,
    active: status.active,
    error,
    blockedHandoff,
    busy,
    start,
    end,
    dismiss,
  };
}

/**
 * A PALETTA SORAI. Kiválasztott cég nélkül a parancs a cégválasztót nyitja —
 * nem indít „valamelyik" cég nevében, és nem tippel.
 */
export function useImpersonationCommands(
  impersonation: ImpersonationHook,
  selectedCompanyId: string | null,
  openPicker: () => void
): Command[] {
  const { allowed, active, start, end } = impersonation;
  return useMemo<Command[]>(() => {
    const commands: Command[] = [];
    if (allowed) {
      commands.push(
        selectedCompanyId === null
          ? { id: 'impersonate', labelKey: 'palette.impersonatePick', run: openPicker }
          : {
              id: 'impersonate',
              labelKey: 'palette.impersonate',
              run: () => start(selectedCompanyId),
            }
      );
    }
    if (active) {
      commands.push({ id: 'impersonation-end', labelKey: 'palette.impersonationEnd', run: end });
    }
    return commands;
  }, [allowed, active, selectedCompanyId, openPicker, start, end]);
}
